import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import RadiusButton from '../../../../components/RadiusButton';

// value of the "All" option; such filters are left out of the request
const ALL_OPTION = '__all__';
export const ALL_VENUES = 'all';

const examFields = [
    { id: 'driveId', label: 'Drive', optionsKey: 'drives' },
    { id: 'courseId', label: 'Course', optionsKey: 'courses' },
    { id: 'examDate', label: 'Exam Date', optionsKey: 'examDates' },
    { id: 'batchId', label: 'Batch', optionsKey: 'batches' },
    { id: 'labNo', label: 'Lab', optionsKey: 'labs' },
];

export const EMPTY_AUDIT_OPTIONS = { venues: [], drives: [], courses: [], examDates: [], batches: [], labs: [], facets: [], summary: [] };

// drives / courses / batches come as { id, name }; exam dates and labs as plain values
const toOption = (option) => (typeof option === 'object' && option !== null
    ? { value: String(option.id), label: option.name }
    : { value: String(option), label: String(option) });

// A dropdown's options: values found in the data that it does not always offer (e.g. RAM sizes),
// in natural order, then the ones it always offers.
const facetOptions = (facet, facetCounts) => {
    const found = Object.keys(facetCounts?.values ?? {}).filter((value) => !facet.options.includes(value))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return [...found, ...facet.options];
};

const FilterSelect = ({ id, label, value, onChange, children, width = 200 }) => (
    <FormControl className='form-field' sx={{ width }}>
        <InputLabel id={`${id}Label`}>{label}</InputLabel>
        <Select labelId={`${id}Label`} id={id} label={label} size='small' value={value} onChange={onChange}>
            {children}
        </Select>
    </FormControl>
);

// Venue, exam filters (which devices are counted) and one dropdown per hardware property, each
// option showing how many devices it would leave given every other filter.
export const HardwareAuditFilters = ({
    venueId, options, examFilters, facetFilters, counts, matchedCount, scopeCount,
    onVenueChange, onExamChange, onFacetChange, onReset, onDownload, isDownloading,
}) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FilterSelect id='venue' label='Venue' value={String(venueId)} width={240}
                onChange={(event) => onVenueChange(event.target.value)}>
                <MenuItem value={ALL_VENUES}>All venues</MenuItem>
                {options.venues.map((venue) => (
                    <MenuItem key={venue.id} value={String(venue.id)}>{venue.venueName}</MenuItem>
                ))}
            </FilterSelect>
            {examFields.map((field) => (
                <FilterSelect key={field.id} id={field.id} label={field.label} value={examFilters[field.id] ?? ALL_OPTION}
                    onChange={(event) => onExamChange(field.id, event.target.value === ALL_OPTION ? undefined : event.target.value)}>
                    <MenuItem value={ALL_OPTION}>All</MenuItem>
                    {(options[field.optionsKey] ?? []).map(toOption).map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </FilterSelect>
            ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            {options.facets.map((facet) => {
                const facetCounts = counts?.[facet.key];
                const countOf = (value) => (facetCounts ? ` (${facetCounts.values?.[value] ?? 0})` : '');
                return (
                    <FilterSelect key={facet.key} id={facet.key} label={facet.name} value={facetFilters[facet.key] ?? ALL_OPTION}
                        onChange={(event) => onFacetChange(facet.key, event.target.value === ALL_OPTION ? undefined : event.target.value)}>
                        <MenuItem value={ALL_OPTION}>All{facetCounts ? ` (${facetCounts.ALL})` : ''}</MenuItem>
                        {facetOptions(facet, facetCounts).map((value) => (
                            <MenuItem key={value} value={value}>{value}{countOf(value)}</MenuItem>
                        ))}
                    </FilterSelect>
                );
            })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontWeight: 500 }}>Filtered Count: {matchedCount}</Typography>
            <Typography sx={{ fontWeight: 500 }}>Total Count: {scopeCount}</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
                <RadiusButton variant='outlined' color='secondary' onClick={onReset}>
                    Reset Filters
                </RadiusButton>
                <RadiusButton variant='contained' color='secondary' onClick={onDownload} disabled={isDownloading || !matchedCount}>
                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                </RadiusButton>
            </Box>
        </Box>
    </Box>
);
